/**
 * GuruMegh.ts — the on-screen dialogue for Guru Megh, the cloud-sage NPC.
 *
 * A small cloud avatar + a speech panel that slides in with a short built-in line. It is
 * screen-fixed, non-intrusive, and never blocks input.
 */
import Phaser from 'phaser';
import type { GuruResponse } from '../telemetry/guruLines';

export class GuruMegh {
  private container?: Phaser.GameObjects.Container;
  private hideEvent?: Phaser.Time.TimerEvent;
  private asyncGeneration = 0;
  /** Where the panel docks vertically. 'sky' = upper area (default); 'bottom' = just
   * above the controls bar, used when a centred status card is on screen so the two
   * never overlap. */
  private dock: 'sky' | 'bottom' = 'sky';

  constructor(private scene: Phaser.Scene) {}

  /** Choose where new lines appear. Existing visible line is re-docked immediately. */
  setDock(dock: 'sky' | 'bottom'): void {
    this.dock = dock;
    if (this.container) {
      this.container.y = this.dockY();
    }
  }

  /** Resolved y-centre for the current dock, clamped to the camera. */
  private dockY(): number {
    const cam = this.scene.cameras.main;
    // 'sky': upper area during normal play — below the top HUD row and the top-right
    //   collectibles panel (which ends at y≈132; this panel is 68px tall so its top at
    //   y≈136 stays clear). Used for spontaneous encouragement.
    // 'bottom': a MID-LOWER band used whenever a centred status card (level-won /
    //   game-over / milestone burst) is on screen. It is deliberately placed BELOW the
    //   status card AND ABOVE the on-screen touch-control buttons (which start at y≈452),
    //   so Guru Megh never overlaps either. On a 540-tall view this lands at y≈386
    //   (panel spans ≈352–420).
    if (this.dock === 'bottom') {
      return Math.min(cam.height - 120, 386);
    }
    return 170;
  }

  /** Show a line from Guru Megh. Replaces any current line. */
  say(text: string, holdMs = 5000): void {
    // Any new line supersedes an in-flight local response.
    this.asyncGeneration += 1;
    this.dismiss();

    const cam = this.scene.cameras.main;
    const W = Math.min(520, cam.width - 60);
    const x = cam.width / 2;
    // Panel is 68px tall (±34). In the 'sky' dock it sits just below the top HUD row
    // (score/lives/time/combo/progress) and the top-right collectibles panel. In the
    // 'bottom' dock it sits above the controls bar so it never covers a centred status
    // card (level-won / game-over), fixing the overlap between the two boxes.
    const y = this.dockY();

    const panel = this.scene.add.graphics();
    panel.fillStyle(0x10233b, 0.9);
    panel.fillRoundedRect(-W / 2, -34, W, 68, 12);
    panel.lineStyle(2, 0x8ecbff, 0.8);
    panel.strokeRoundedRect(-W / 2, -34, W, 68, 12);

    // Cloud-sage avatar (a soft cloud with a serene face) on the left.
    const avatar = this.scene.add.graphics();
    const ax = -W / 2 + 30;
    avatar.fillStyle(0xffffff, 0.95);
    avatar.fillCircle(ax, 0, 16);
    avatar.fillCircle(ax + 12, 6, 12);
    avatar.fillCircle(ax - 12, 6, 11);
    avatar.fillCircle(ax + 2, -10, 10);
    avatar.fillStyle(0x10233b, 1);
    avatar.fillCircle(ax - 4, 0, 1.6);
    avatar.fillCircle(ax + 4, 0, 1.6);
    avatar.lineStyle(1.5, 0x10233b, 1);
    avatar.beginPath();
    avatar.arc(ax, 3, 4, Phaser.Math.DegToRad(20), Phaser.Math.DegToRad(160));
    avatar.strokePath();

    const name = this.scene.add.text(-W / 2 + 56, -26, 'Guru Megh', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      color: '#8ecbff',
      fontStyle: 'bold',
    });

    const body = this.scene.add.text(-W / 2 + 56, -8, text, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '15px',
      color: '#eaf4ff',
      wordWrap: { width: W - 72 },
    });

    const container = this.scene.add.container(x, y, [panel, avatar, name, body]);
    container.setScrollFactor(0).setDepth(3000).setAlpha(0);
    this.container = container;

    // Slide down + fade in (descends gently from above, like a cloud settling in the sky).
    container.y = y - 20;
    this.scene.tweens.add({ targets: container, y, alpha: 1, duration: 260, ease: 'Quad.out' });

    this.hideEvent = this.scene.time.delayedCall(holdMs, () => this.dismiss());
  }

  /** Show a placeholder immediately, then replace it only if this scene/request is still current. */
  sayAsync(promise: Promise<GuruResponse>, holdMs = 5000): void {
    this.say('…', 1500);
    const requestGeneration = this.asyncGeneration;
    void promise
      .then((response) => {
        if (requestGeneration !== this.asyncGeneration) return;
        if (!this.scene.sys.isActive()) return;
        this.say(response.text, holdMs);
      })
      .catch(() => {
        // Local Guru lines are resilient; never create an unhandled UI rejection.
      });
  }

  dismiss(): void {
    this.hideEvent?.remove(false);
    this.hideEvent = undefined;
    if (!this.container) return;
    const c = this.container;
    this.container = undefined;
    this.scene.tweens.add({
      targets: c,
      alpha: 0,
      y: c.y - 16,
      duration: 220,
      onComplete: () => c.destroy(),
    });
  }
}
